import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { useModalDialog } from "../../../hooks/useModalDialog";
import { usePresence } from "../../../hooks/usePresence";
import { OVERLAY_EXIT_MS } from "../../../design-system/tokens/overlayMotion";
import { ShortcutKeys } from "../../atoms/ShortcutKeys";
import { CommandList, getCommandOptionId } from "../../molecules/CommandList";
import type { CommandListItem } from "../../molecules/CommandList";
import {
  Body,
  Crumb,
  Dialog,
  Footer,
  HintItem,
  Hints,
  HintsEnd,
  HiddenTitle,
  Root,
  Scrim,
  SearchEnd,
  SearchIcon,
  SearchInput,
  SearchRow,
} from "./CommandPalette.styles";
import type { CommandPaletteHint, CommandPaletteProps } from "./CommandPalette.types";

const isDisabled = (item: CommandListItem): boolean => Boolean(item.disabled || item.soon || item.disabledReason);

/** A modal combobox over a grouped listbox: type to search, arrows to move, Enter to choose. */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  insetLeft,
  title,
  placeholder,
  searchLabel,
  emptyLabel,
  query,
  onQueryChange,
  groups,
  onSelect,
  onClose,
  crumb,
  onBack,
  searchEnd,
  hints,
  endHints,
  idPrefix = "command-palette",
  mac,
}) => {
  const searchId = `${idPrefix}-search`;
  const listboxId = `${idPrefix}-list`;
  const dialog = useModalDialog(open, onClose, searchId);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const { mounted } = usePresence(open, OVERLAY_EXIT_MS);

  // Rows of a Soon group can be seen but never run.
  const items = useMemo(() => groups.flatMap((group) => group.items.map((item) => (group.soon ? { ...item, disabled: true } : item))), [groups]);
  // The first row you can run is active until you move; a list of disabled rows starts on its first.
  const fallbackId = (items.find((item) => !isDisabled(item)) ?? items[0])?.id ?? null;
  const activeId = pickedId !== null && items.some((item) => item.id === pickedId) ? pickedId : fallbackId;

  // A new query or step starts from the top again.
  useEffect(() => setPickedId(null), [query, crumb, open]);

  useEffect(() => {
    if (!open || !activeId) return;
    const node = document.getElementById(getCommandOptionId(listboxId, activeId));
    if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ block: "nearest" });
  }, [open, activeId, listboxId]);

  const choose = (id: string | null) => {
    const item = items.find((candidate) => candidate.id === id);
    if (item && !isDisabled(item)) onSelect(item.id);
  };

  const move = (to: number) => {
    if (items.length === 0) return;
    setPickedId(items[((to % items.length) + items.length) % items.length].id);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    const index = items.findIndex((item) => item.id === activeId);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(index < 0 ? items.length - 1 : index - 1);
        break;
      case "Home":
        event.preventDefault();
        move(0);
        break;
      case "End":
        event.preventDefault();
        move(items.length - 1);
        break;
      case "Enter": {
        event.preventDefault();
        // Mod+Enter runs the row that shows that chord (e.g. "Ask"), wherever the active row is.
        if (event.metaKey || event.ctrlKey) {
          const modRow = items.find((item) => item.shortcut?.some((chord) => chord.key === "Enter" && chord.mod));
          if (modRow) {
            choose(modRow.id);
            break;
          }
        }
        choose(activeId);
        break;
      }
      case "Backspace":
        if (query === "" && crumb && onBack) {
          event.preventDefault();
          onBack();
        }
        break;
      default:
    }
  };

  const hasItems = items.length > 0;
  const name = searchLabel ?? title;
  const renderHints = (list: readonly CommandPaletteHint[]) =>
    list.map((hint) => (
      <HintItem key={hint.label}>
        <ShortcutKeys chords={hint.chords} mac={mac} merged />
        {hint.label}
      </HintItem>
    ));

  if (!mounted) return null;
  const closing = !open;
  return createPortal(
    <Root $insetLeft={insetLeft}>
      <Scrim $closing={closing} data-testid="dialog-scrim" onMouseDown={onClose} />
      <Dialog $closing={closing} ref={dialog.dialogRef} role="dialog" aria-modal="true" aria-labelledby={`${idPrefix}-title`} onKeyDown={dialog.onKeyDown}>
        <HiddenTitle id={`${idPrefix}-title`} ref={dialog.titleRef} tabIndex={-1}>
          {title}
        </HiddenTitle>
        <SearchRow>
          <SearchIcon>
            <Search size={18} aria-hidden="true" />
          </SearchIcon>
          {crumb ? <Crumb>{crumb}</Crumb> : null}
          <SearchInput
            id={searchId}
            type="text"
            role="combobox"
            aria-label={name}
            aria-expanded={hasItems}
            aria-controls={hasItems ? listboxId : undefined}
            aria-activedescendant={hasItems && activeId ? getCommandOptionId(listboxId, activeId) : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            placeholder={placeholder}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={onSearchKeyDown}
          />
          {searchEnd ? <SearchEnd>{searchEnd}</SearchEnd> : null}
        </SearchRow>
        <Body>
          <CommandList
            groups={groups}
            activeId={activeId}
            listboxId={listboxId}
            label={name}
            emptyLabel={emptyLabel}
            mac={mac}
            onSelect={choose}
            onActiveChange={setPickedId}
          />
        </Body>
        {(hints && hints.length > 0) || (endHints && endHints.length > 0) ? (
          <Footer>
            {hints && hints.length > 0 ? <Hints>{renderHints(hints)}</Hints> : null}
            {endHints && endHints.length > 0 ? <HintsEnd>{renderHints(endHints)}</HintsEnd> : null}
          </Footer>
        ) : null}
      </Dialog>
    </Root>,
    document.body,
  );
};

export default CommandPalette;
